/**
 * 邀请有礼页面
 * 用户可以生成邀请链接、查看邀请统计和历史记录
 * 黑白主题风格（Vercel 风格）
 */

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Gift,
  Loader2,
  Copy,
  Check,
  Users,
  UserPlus,
  Calendar,
  Award,
  Share2,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import SubsectionHeader from "@/components/shared/SubsectionHeader";
import {
  invitationService,
  type Invitation,
  type InvitationStats,
} from "@/services/invitationService";

/**
 * 邀请有礼页面
 */
export default function Invitations() {
  const { t, i18n } = useTranslation();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [stats, setStats] = useState<InvitationStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [currentInviteUrl, setCurrentInviteUrl] = useState<string>("");

  // 加载邀请数据
  const loadInvitations = async () => {
    setIsLoading(true);
    try {
      const data = await invitationService.getInvitations();
      setInvitations(data.invitations || []);
      setStats(data.stats || null);

      // 设置当前邀请链接（最新的待处理邀请）
      const pendingInvitation = data.invitations?.find(
        (inv: Invitation) => inv.status === "pending",
      );
      if (pendingInvitation) {
        const baseUrl = window.location.origin;
        // 直接使用 SPA 注册页面链接
        setCurrentInviteUrl(
          `${baseUrl}/register?invite_token=${pendingInvitation.token}`,
        );
      }
    } catch (error) {
      console.error("加载邀请数据失败:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("invitations.toast.loadFailed"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // 生成新邀请
  const generateInvitation = async () => {
    setIsGenerating(true);
    try {
      const data = await invitationService.createInvitation();
      toast.success(t("invitations.toast.generateSuccess"));
      setCurrentInviteUrl(data.invite_url);
      // 重新加载列表
      await loadInvitations();
    } catch (error) {
      console.error("生成邀请失败:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("invitations.toast.generateFailed"),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // 复制邀请链接
  const copyInviteUrl = async (url: string, token: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success(t("invitations.toast.linkCopied"));
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      console.error("复制失败:", error);
      toast.error(t("invitations.toast.copyFailed"));
    }
  };

  // 复制邀请码
  const copyInviteCode = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(token);
      toast.success(t("invitations.toast.codeCopied"));
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      console.error("复制失败:", error);
      toast.error(t("invitations.toast.copyFailed"));
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    loadInvitations();
  }, []);

  // 格式化日期
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const locale =
      i18n.language === "zh"
        ? "zh-CN"
        : i18n.language === "jp"
          ? "ja-JP"
          : "en-US";
    return new Date(dateString).toLocaleDateString(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 状态徽章
  const StatusBadge = ({ status }: { status: string }) => {
    const statusTextMap: Record<string, string> = {
      pending: t("invitations.status.pending"),
      accepted: t("invitations.status.accepted"),
      expired: t("invitations.status.expired"),
    };
    const variantMap: Record<string, "default" | "secondary" | "outline"> = {
      pending: "secondary",
      accepted: "outline",
      expired: "secondary",
    };
    return (
      <Badge
        variant={variantMap[status] || "secondary"}
        className={status === "accepted" ? "border-foreground/20" : ""}
      >
        {statusTextMap[status] || statusTextMap.pending}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-4 sm:py-6">
      {/* 页面标题 - 居中 */}
      <SubsectionHeader
        title={t("invitations.title")}
        icon="Gift"
        description={t("invitations.description")}
        className="mb-8"
      />

      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-muted rounded-lg w-fit">
                  <Share2 className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t("invitations.stats.totalSent")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-foreground">
                    {stats.total_sent}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-muted rounded-lg w-fit">
                  <UserPlus className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t("invitations.stats.accepted")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-foreground">
                    {stats.total_accepted}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-muted rounded-lg w-fit">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t("invitations.stats.monthlyInvited")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-foreground">
                    {stats.monthly_accepted}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-muted rounded-lg w-fit">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t("invitations.stats.invitedFriends")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-foreground">
                    {stats.invited_users_count ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 生成邀请卡片 */}
      <Card className="mb-6">
        <CardContent className="p-5 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
            <div className="flex-shrink-0 p-3 sm:p-4 bg-muted rounded-xl w-fit">
              <Award className="h-6 w-6 sm:h-8 sm:w-8 text-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-xl font-bold text-foreground mb-1 sm:mb-2">
                {t("invitations.generate.title")}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-4">
                {t("invitations.generate.description")}
              </p>

              {currentInviteUrl ? (
                <div className="space-y-3">
                  <Input
                    value={currentInviteUrl}
                    readOnly
                    className="bg-muted/50 text-sm"
                  />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={() =>
                        copyInviteUrl(
                          currentInviteUrl,
                          invitations[0]?.token || "",
                        )
                      }
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      {copiedToken === invitations[0]?.token ? (
                        <Check className="h-4 w-4 mr-2" />
                      ) : (
                        <Copy className="h-4 w-4 mr-2" />
                      )}
                      {t("invitations.generate.copyLink")}
                    </Button>
                    <Button
                      onClick={generateInvitation}
                      disabled={isGenerating}
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("invitations.generate.generating")}
                        </>
                      ) : (
                        <>
                          <Share2 className="mr-2 h-4 w-4" />
                          {t("invitations.generate.generateNew")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={generateInvitation}
                  disabled={isGenerating}
                  className="w-full sm:w-auto"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("invitations.generate.generating")}
                    </>
                  ) : (
                    <>
                      <Share2 className="mr-2 h-4 w-4" />
                      {t("invitations.generate.generateLink")}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 邀请记录 */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="mb-4">
            <h3 className="text-base sm:text-lg font-semibold">
              {t("invitations.records.title")}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {t("invitations.records.description")}
            </p>
          </div>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex flex-col sm:flex-row sm:items-center py-3 px-4 border-b gap-2"
                >
                  <Skeleton className="h-6 w-24 rounded" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-32 sm:ml-auto" />
                </div>
              ))}
            </div>
          ) : invitations.length === 0 ? (
            <div className="py-8 sm:py-12 text-center">
              <Gift className="h-12 w-12 sm:h-16 sm:w-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-muted-foreground">
                {t("invitations.records.noRecords")}
              </p>
            </div>
          ) : (
            <>
              {/* 移动端卡片列表 */}
              <div className="sm:hidden space-y-3">
                {invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="p-4 bg-muted/30 rounded-lg border border-border/50"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                        {invitation.token}
                      </code>
                      <StatusBadge status={invitation.status} />
                    </div>
                    {invitation.invitee_name && (
                      <div className="text-sm text-foreground mb-2">
                        <span className="text-muted-foreground">
                          {t("invitations.records.invitee")}:{" "}
                        </span>
                        {invitation.invitee_name}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>
                        {t("invitations.records.createdAt")}:{" "}
                        {formatDate(invitation.created_at)}
                      </div>
                      {invitation.accepted_at && (
                        <div>
                          {t("invitations.records.acceptedAt")}:{" "}
                          {formatDate(invitation.accepted_at)}
                        </div>
                      )}
                    </div>
                    {invitation.status === "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyInviteCode(invitation.token)}
                        className="w-full mt-3"
                      >
                        {copiedToken === invitation.token ? (
                          <Check className="h-4 w-4 mr-1" />
                        ) : (
                          <Copy className="h-4 w-4 mr-1" />
                        )}
                        {t("invitations.records.copy")}
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* 桌面端表格 */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        {t("invitations.records.inviteCode")}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        {t("invitations.records.status")}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        {t("invitations.records.invitee")}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        {t("invitations.records.createdAt")}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        {t("invitations.records.acceptedAt")}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        {t("invitations.records.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((invitation) => (
                      <tr
                        key={invitation.id}
                        className="border-b hover:bg-muted/50 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <code className="text-sm bg-muted px-2 py-1 rounded font-mono">
                            {invitation.token}
                          </code>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={invitation.status} />
                        </td>
                        <td className="py-3 px-4 text-sm text-foreground">
                          {invitation.invitee_name || "-"}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {formatDate(invitation.created_at)}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {formatDate(invitation.accepted_at)}
                        </td>
                        <td className="py-3 px-4">
                          {invitation.status === "pending" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyInviteCode(invitation.token)}
                            >
                              {copiedToken === invitation.token ? (
                                <Check className="h-4 w-4 mr-1" />
                              ) : (
                                <Copy className="h-4 w-4 mr-1" />
                              )}
                              {t("invitations.records.copy")}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* 奖励说明 */}
      <Card className="mt-4 sm:mt-6">
        <CardContent className="p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2 mb-3 sm:mb-4">
            <Award className="h-4 w-4 sm:h-5 sm:w-5" />
            {t("invitations.rewards.title")}
          </h3>
          <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground">
            <li>• {t("invitations.rewards.rule1")}</li>
            <li>• {t("invitations.rewards.rule2")}</li>
            <li>• {t("invitations.rewards.rule3")}</li>
            <li>• {t("invitations.rewards.rule4")}</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
